import React from 'react';
import { LayoutTemplate } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogFooter } from '@/ui-components/ui/dialog';
import { Button } from '@/ui-components/ui/button';
import { StandardDialogHeader } from '@/util-components/dialog/components/StandardDialogHeader';
import { DefaultTemplateType } from '@/page-components/post-generator/template-editor/registry/defaultTemplates';

interface MissingTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: DefaultTemplateType;
  label: string;
  description?: string;
}

/**
 * Shared dialog for when a required default template is not yet assigned.
 * Consolidates duplicate logic from the customer dashboard.
 */
export const MissingTemplateDialog: React.FC<MissingTemplateDialogProps> = ({
  open,
  onOpenChange,
  type,
  label,
  description,
}) => {
  const navigate = useNavigate();

  const handleGoToDefaults = () => {
    onOpenChange(false);
    navigate(`/post-generator/default-templates/${type}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg p-0 pt-[max(0.5rem,env(safe-area-inset-top))] pb-[max(0.5rem,env(safe-area-inset-bottom))] border-0 shadow-2xl left-0 top-0 translate-x-0 translate-y-0 max-w-none max-h-none h-[100vh] w-screen flex flex-col overflow-hidden gap-0 rounded-none sm:left-[50%] sm:top-[50%] sm:h-auto sm:w-full sm:max-h-[85vh] sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-3xl sm:pt-0 sm:pb-0">
        <div className="px-5 sm:px-6 py-4 sm:py-5 shrink-0">
          <StandardDialogHeader
            icon={LayoutTemplate}
            title="Standard-Template erforderlich"
            description={`Für ${label} muss zuerst ein Standard-Template festgelegt werden.`}
            className="p-0 border-none"
          />
        </div>
        
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2 space-y-5 sm:space-y-6">
            <p className="text-sm text-gray-600 leading-relaxed font-medium">
              {description || 
                `Wählen Sie ein Standard-Template für ${label}. Danach können Sie direkt aus dem Dashboard heraus professionelle Grafiken erstellen.`}
            </p>
          </div>
        </div>

        <DialogFooter className="px-5 sm:px-6 py-4 shrink-0 border-t border-gray-100 bg-gradient-to-t from-background via-background to-background/95 !flex-col gap-3 sm:!flex-row sm:items-center sm:justify-between sm:gap-4">
          <div className="flex w-full sm:w-auto">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="h-11 w-full justify-center rounded-xl px-4 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100 hover:text-gray-900 sm:w-auto sm:justify-start"
            >
              Abbrechen
            </Button>
          </div>
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
            <Button
              onClick={handleGoToDefaults}
              className="h-11 w-full px-6 sm:px-8 min-w-[140px] rounded-xl font-bold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all sm:w-auto"
            >
              Template auswählen
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
